// 位圖數字字型載入 — 沿用 Unity 的位圖字型圖集：
//   win        tx_win.png        贏分數字（大獎慶祝金額／盤面浮動字），glyph 高 206px
//   winWide    tx_win1.png       組合方式（ways）數字（左上紅木牌 WaysValueText），glyph 高 178px，含 +/,/./0-9/B/K/M/X
//   multiplier tx_Multiplier.png 免費遊戲倍率數字（倍率吊牌／主倍率飛入），glyph 高 64px，含 X(0x58)
//   freeSpin   tx_freeSpin.png   免費遊戲次數數字（進場字卡次數），glyph 高 156px，含 X(0x58)
//   multiple   tx_multiple.png   浮動計分（WinTemp）數字，glyph 高 70px（逗號 72px），含 X/B/K/M/$
//   marquee    tx_marquee.png    底部列自動遊戲剩餘次數，含 +/,/./0-9/B/K/M/X
//   loading    tx_loading.png    遊戲 loading 輪播的「30,000X」，glyph 高一律 182px，含 ,/./0-9/X
//
// win／multiplier／freeSpin 是 Slot026 專屬（`Art/Fonts`，隨附 image_font_to_textMeshPro 產生的 .json）；
// **winWide／multiple／marquee／loading 是 SlotFramework 共用字型**（`SlotFramework/Art/Font/Common/`，只有
// .asset＋.png，沒有 .json），其 .json 由 `scripts/parse-tmp-sprite-font.mjs` 從 TMP_SpriteAsset 轉出。
// ⚠️ `tx_win1`（ways 用的金色寬版贏分字）與 `tx_win`（贏分字）是**兩張不同的圖**：Unity WaysValueText 的
//    spriteAsset 是 `tx_win1_width.asset`（寬版 glyph metrics），底圖 `tx_win1.png`，glyph 高 178（tx_win 是 206）。
// ⚠️ `tx_multiple`（粉紫色、256×512）與 `tx_Multiplier`（Slot026 倍率牌用）是**兩套不同字型**，別互換。
//
// 各 .json 為精靈圖集（TexturePacker 風格），每個 frame 的 filename 是字元的十六進位 ASCII 碼
// （30~39=數字 0-9、2C=逗號、2E=句點、2B=加號、58=X）。
// 載入後以「字元 → 子貼圖(frame)＋寬度」對照表提供給 BitmapNumber 逐字排版。
//
// ⚠️ 減少請求數的兩個接法（見 framework/assets/spriteAtlas.ts）：
//   - 底圖已併入合圖，故 `base` 可能是合圖的子貼圖，glyph 座標要加上底圖在合圖內的原點。
//     **tx_loading 併的是 loading 合圖**（遊戲 loading 版面比 ui 合圖早一大段時間就要顯示），
//     其餘 5 張併 ui 合圖 —— 故每套字型各自記錄自己的合圖組別（`atlas`）。
//   - 各份 .json 由 scripts/pack-atlases.mjs 合併為 `/images/atlas/bitmapFonts.json`（1 筆請求）；
//     合併檔不可用時自動退回逐檔 fetch。

import { Rectangle, Texture } from 'pixi.js'
import { ensureAtlases, loadTexture } from '../../framework/assets/spriteAtlas'
import { logger } from '../../framework/core/Logger'

export type NumberFontName = 'win' | 'winWide' | 'multiplier' | 'freeSpin' | 'multiple' | 'marquee' | 'loading'

const FONT_URLS: Record<NumberFontName, { readonly image: string; readonly json: string; readonly atlas: string }> = {
  win: { image: '/images/fonts/tx_win.webp', json: '/images/fonts/tx_win.json', atlas: 'ui' },
  winWide: { image: '/images/fonts/tx_win1.webp', json: '/images/fonts/tx_win1.json', atlas: 'ui' },
  multiplier: { image: '/images/fonts/tx_Multiplier.webp', json: '/images/fonts/tx_Multiplier.json', atlas: 'ui' },
  freeSpin: { image: '/images/fonts/tx_freeSpin.webp', json: '/images/fonts/tx_freeSpin.json', atlas: 'ui' },
  multiple: { image: '/images/fonts/tx_multiple.webp', json: '/images/fonts/tx_multiple.json', atlas: 'ui' },
  marquee: { image: '/images/fonts/tx_marquee.webp', json: '/images/fonts/tx_marquee.json', atlas: 'ui' },
  loading: { image: '/images/loading/tx_loading.webp', json: '/images/fonts/tx_loading.json', atlas: 'loading' },
}

interface Glyph {
  readonly texture: Texture
  readonly width: number
  readonly height: number
}

interface AtlasFrame {
  readonly filename: string
  readonly frame: { readonly x: number; readonly y: number; readonly w: number; readonly h: number }
}

interface LoadedFont {
  readonly glyphs: ReadonlyMap<string, Glyph>
  readonly height: number
}

const fonts = new Map<NumberFontName, LoadedFont>()

/** 合併後的 glyph 表（由 scripts/pack-atlases.mjs 產生，key 為字型檔名如 `tx_win`）。 */
const MERGED_GLYPHS_URL = '/images/atlas/bitmapFonts.json'

/** 字型在合併 glyph 表裡的 key（＝原 .json 的檔名主幹）。 */
function glyphTableKey(name: NumberFontName): string {
  return FONT_URLS[name].json.replace(/^.*\//, '').replace(/\.json$/, '')
}

type GlyphTables = Record<NumberFontName, readonly AtlasFrame[]>

/** 取全部字型的 glyph 表：優先讀合併檔（1 筆請求），不可用才逐檔 fetch（5 筆）。 */
async function fetchGlyphTables(): Promise<GlyphTables> {
  const names = Object.keys(FONT_URLS) as NumberFontName[]
  const tables = {} as Record<NumberFontName, readonly AtlasFrame[]>
  try {
    const response = await fetch(MERGED_GLYPHS_URL)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const merged = (await response.json()) as Record<string, readonly AtlasFrame[] | undefined>
    const missing = names.filter((name) => merged[glyphTableKey(name)] === undefined)
    if (missing.length > 0) throw new Error(`合併 glyph 表缺少：${missing.join(', ')}`)
    for (const name of names) tables[name] = merged[glyphTableKey(name)] ?? []
    return tables
  } catch (error) {
    logger.warn('合併 glyph 表不可用，改逐檔載入（請檢查是否已跑過 npm run atlas:pack）', error)
    await Promise.all(
      names.map(async (name) => {
        const response = await fetch(FONT_URLS[name].json)
        const data = (await response.json()) as { frames: AtlasFrame[] }
        tables[name] = data.frames
      }),
    )
    return tables
  }
}

/** glyph 表只取一次（loadNumberFont/loadNumberFonts 可任意併發或重複呼叫）。 */
let glyphTables: Promise<GlyphTables> | null = null
function glyphTablesOnce(): Promise<GlyphTables> {
  glyphTables ??= fetchGlyphTables()
  return glyphTables
}

async function loadOne(name: NumberFontName, frames: readonly AtlasFrame[]): Promise<void> {
  if (fonts.has(name)) return
  const base = await loadTexture(FONT_URLS[name].image)
  // 底圖併入 ui 合圖後 base 是子貼圖：glyph 的 frame 是「相對底圖左上角」，必須補上底圖在合圖內的
  // 原點，否則會取到隔壁貼圖。單獨載入時 base.frame 就是 (0,0,w,h)，加 0 等於原行為。
  const originX = base.frame.x
  const originY = base.frame.y

  const map = new Map<string, Glyph>()
  let height = 0
  for (const entry of frames) {
    const code = Number.parseInt(entry.filename.replace('.png', ''), 16)
    if (Number.isNaN(code)) continue
    const char = String.fromCharCode(code)
    const texture = new Texture({
      source: base.source,
      frame: new Rectangle(originX + entry.frame.x, originY + entry.frame.y, entry.frame.w, entry.frame.h),
    })
    map.set(char, { texture, width: entry.frame.w, height: entry.frame.h })
    height = Math.max(height, entry.frame.h)
  }
  fonts.set(name, { glyphs: map, height })
}

/** 載入全部位圖字型（圖集底圖 + glyph frame 表）。第一次畫面前呼叫（見 main.ts）。 */
export async function loadNumberFont(): Promise<void> {
  await loadNumberFonts(...(Object.keys(FONT_URLS) as NumberFontName[]))
}

/**
 * 只載入指定的幾套位圖字型（每套各自快取、可重複呼叫）。
 * 供遊戲 loading 版面在「其餘資源尚未下載」時就先取得它需要的那一套（見 LoadingAssets）。
 */
export async function loadNumberFonts(...names: readonly NumberFontName[]): Promise<void> {
  // 字型底圖併在合圖裡（與 loadUiTextures／loadLoadingAssets 併發呼叫也只會各發一筆請求）。
  const groups = [...new Set(names.map((name) => FONT_URLS[name].atlas))]
  const [tables] = await Promise.all([glyphTablesOnce(), ensureAtlases(...groups)])
  await Promise.all(names.map((name) => loadOne(name, tables[name])))
}

/** 取得某字型某字元的位圖 glyph（無對應字元回 null）。 */
export function getNumberGlyph(char: string, font: NumberFontName = 'win'): { readonly texture: Texture; readonly width: number } | null {
  return fonts.get(font)?.glyphs.get(char) ?? null
}

/** 位圖字型的 glyph 原生高度（供 BitmapNumber 換算縮放）；未載入時回 0。 */
export function numberFontHeight(font: NumberFontName = 'win'): number {
  return fonts.get(font)?.height ?? 0
}

/** 是否已載入。 */
export function isNumberFontLoaded(font: NumberFontName = 'win'): boolean {
  return (fonts.get(font)?.height ?? 0) > 0
}
